import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import useSetDefaultCurrency from '@/features/dashboard/paymentConfig/hooks/useSetDefaultCurrency.ts';
import {
  defaultCurrencySchema,
  DefaultCurrencyFormData,
} from '@/features/dashboard/paymentConfig/schemas/defaultCurrencySchema.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { currencySelectOptions } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';

interface EditDefaultCurrencyModalProps {
  currentCurrency?: Currency;
  onClose: () => void;
}

const EditDefaultCurrencyModal = ({
  currentCurrency,
  onClose,
}: EditDefaultCurrencyModalProps) => {
  const setMutation = useSetDefaultCurrency();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DefaultCurrencyFormData>({
    resolver: zodResolver(defaultCurrencySchema),
    defaultValues: { currency: currentCurrency },
  });

  const onSubmit = (data: DefaultCurrencyFormData) => {
    setMutation.mutate(data, { onSuccess: () => onClose() });
  };

  return (
    <Modal onClose={onClose} className="max-w-[440px]">
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader title="Edytuj domyślną walutę" onClose={onClose} />
        <ModalContent>
          <SingleSelectInput
            fieldName="currency"
            control={control}
            label="Waluta"
            required
            options={currencySelectOptions}
            error={errors.currency?.message}
          />
        </ModalContent>
        <ModalFooter>
          <ActionButton tone="ghost" size="sm" type="button" onClick={onClose}>
            Anuluj
          </ActionButton>
          <ActionButton
            tone="accent"
            size="sm"
            type="submit"
            isLoading={setMutation.isPending}
          >
            Zapisz
          </ActionButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditDefaultCurrencyModal;
